import React from 'react'
import ReactSelect, { type StylesConfig } from 'react-select'
import { colors, radius, fontFamily, fontSize, componentHeight, transition, focusRing } from '../../styles/theme'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  inputSize?: 'sm' | 'md' | 'lg'
}

export function Input({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  inputSize = 'md',
  style,
  id,
  ...rest
}: InputProps) {
  const [focused, setFocused] = React.useState(false)
  const autoId = React.useId()
  const inputId = id ?? autoId
  const height = inputSize === 'sm' ? componentHeight.inputSm : inputSize === 'lg' ? componentHeight.inputLg : componentHeight.inputMd
  const fs = inputSize === 'sm' ? fontSize.xs : inputSize === 'lg' ? fontSize.md : fontSize.sm

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label htmlFor={inputId} style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {leftIcon && (
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: colors.textMuted, display: 'flex', alignItems: 'center', pointerEvents: 'none',
          }}>
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          style={{
            display: 'block',
            width: '100%',
            height,
            padding: `0 ${rightIcon ? '36px' : '12px'} 0 ${leftIcon ? '34px' : '12px'}`,
            fontFamily: fontFamily.sans,
            fontSize: fs,
            color: colors.text,
            background: colors.surface,
            border: `1px solid ${error ? colors.errorBorder : focused ? colors.primary : colors.border}`,
            borderRadius: radius.md,
            boxShadow: focused ? focusRing : 'none',
            outline: 'none',
            transition: `border-color ${transition.fast}, box-shadow ${transition.fast}`,
            letterSpacing: '-0.01em',
            ...style,
          }}
          onFocus={e => { setFocused(true); rest.onFocus?.(e) }}
          onBlur={e => { setFocused(false); rest.onBlur?.(e) }}
          {...rest}
        />
        {rightIcon && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            color: colors.textMuted, display: 'flex', alignItems: 'center',
          }}>
            {rightIcon}
          </span>
        )}
      </div>
      {error && <span style={{ fontSize: fontSize.xs, color: colors.error }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{hint}</span>}
    </div>
  )
}

export interface SelectOption {
  value: string | number
  label: React.ReactNode
}

interface SelectProps {
  label?: string
  error?: string
  hint?: string
  inputSize?: 'sm' | 'md' | 'lg'
  options: SelectOption[]
  value?: string | number | null
  onChange: (value: string | number) => void
  placeholder?: string
  isDisabled?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  name?: string
  /** Id of the underlying input, for an external <label htmlFor>. Generated when omitted. */
  id?: string
}

export function Select({
  label, error, hint, inputSize = 'md',
  options, value, onChange, placeholder, isDisabled, isClearable, isSearchable = false, name, id,
}: SelectProps) {
  const height = inputSize === 'sm' ? componentHeight.inputSm : inputSize === 'lg' ? componentHeight.inputLg : componentHeight.inputMd
  const fs = inputSize === 'sm' ? fontSize.xs : inputSize === 'lg' ? fontSize.md : fontSize.sm
  const selected = options.find(o => o.value === value) ?? null
  const autoId = React.useId()
  const inputId = id ?? autoId

  const styles: StylesConfig<SelectOption, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: height,
      fontFamily: fontFamily.sans,
      fontSize: fs,
      background: colors.surface,
      borderColor: error ? colors.errorBorder : state.isFocused ? colors.primary : colors.border,
      borderRadius: radius.md,
      boxShadow: state.isFocused ? focusRing : 'none',
      letterSpacing: '-0.01em',
      cursor: 'pointer',
      '&:hover': { borderColor: error ? colors.errorBorder : colors.primary },
    }),
    valueContainer: base => ({ ...base, padding: '0 12px' }),
    input: base => ({ ...base, margin: 0, padding: 0, color: colors.text }),
    singleValue: base => ({ ...base, color: colors.text }),
    placeholder: base => ({ ...base, color: colors.textMuted }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: base => ({ ...base, color: colors.textMuted, padding: '0 8px' }),
    clearIndicator: base => ({ ...base, color: colors.textMuted, padding: '0 4px' }),
    menu: base => ({ ...base, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden', zIndex: 20 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontFamily: fontFamily.sans,
      fontSize: fs,
      background: state.isSelected ? colors.primary : state.isFocused ? colors.primaryLight : 'transparent',
      color: state.isSelected ? colors.onPrimary : colors.text,
      cursor: 'pointer',
    }),
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label htmlFor={inputId} style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <ReactSelect<SelectOption, false>
        inputId={inputId}
        name={name}
        options={options}
        value={selected}
        onChange={opt => onChange(opt ? opt.value : '')}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isSearchable={isSearchable}
        menuPortalTarget={document.body}
        styles={styles}
      />
      {error && <span style={{ fontSize: fontSize.xs, color: colors.error }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{hint}</span>}
    </div>
  )
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export function Textarea({ label, error, hint, style, id, ...rest }: TextareaProps) {
  const [focused, setFocused] = React.useState(false)
  const autoId = React.useId()
  const textareaId = id ?? autoId

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label htmlFor={textareaId} style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, letterSpacing: '-0.01em' }}>
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        style={{
          display: 'block',
          width: '100%',
          padding: '10px 12px',
          fontFamily: fontFamily.sans,
          fontSize: fontSize.sm,
          color: colors.text,
          background: colors.surface,
          border: `1px solid ${error ? colors.errorBorder : focused ? colors.primary : colors.border}`,
          borderRadius: radius.md,
          boxShadow: focused ? focusRing : 'none',
          outline: 'none',
          resize: 'vertical',
          lineHeight: 1.5,
          letterSpacing: '-0.01em',
          ...style,
        }}
        onFocus={e => { setFocused(true); rest.onFocus?.(e) }}
        onBlur={e => { setFocused(false); rest.onBlur?.(e) }}
        {...rest}
      />
      {error && <span style={{ fontSize: fontSize.xs, color: colors.error }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{hint}</span>}
    </div>
  )
}
